import type React from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowForward,
  CalendarTodayOutlined,
  CategoryOutlined,
  Close,
  HowToReg,
  LocationOnOutlined,
  PersonOutline,
  PhoneOutlined,
} from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../components/common/ShowToast";
import {
  BookingStatusChangeRequest,
  fetchMyPendingApprovals,
  approveStatusChangeRequest,
  declineStatusChangeRequest,
} from "./statusChangeRequestStorage";

const BACKSTOP_POLL_INTERVAL_MS = 3 * 60 * 1000;
const DISMISSED_KEY = "dismissed-status-change-requests";

const capitalizeStatus = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

function InfoRow({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          display: "flex",
          color: "text.secondary",
          "& svg": { fontSize: 16 },
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: 12.5 }}>{text}</Typography>
    </Box>
  );
}

function DetailLine({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600, textAlign: "right" }}>
        {value}
      </Typography>
    </Box>
  );
}

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const readDismissed = (): number[] => {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || "[]");
  } catch {
    return [];
  }
};

const dismiss = (requestId: number) => {
  const dismissed = readDismissed();
  if (!dismissed.includes(requestId)) {
    sessionStorage.setItem(
      DISMISSED_KEY,
      JSON.stringify([...dismissed, requestId]),
    );
  }
};

export default function PendingApprovalWatcher() {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isAdmin = currentUser?.role === "admin";
  const location = useLocation();

  const [queue, setQueue] = useState<BookingStatusChangeRequest[]>([]);
  const [active, setActive] = useState<BookingStatusChangeRequest | null>(
    null,
  );
  const [deciding, setDeciding] = useState(false);
  const [decliningReason, setDecliningReason] = useState<string | null>(null);
  const [deceasedAnchorEl, setDeceasedAnchorEl] =
    useState<HTMLElement | null>(null);
  const [contactAnchorEl, setContactAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  const checkRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!isAdmin) {
      setQueue([]);
      return;
    }

    let cancelled = false;
    const check = async () => {
      try {
        const dismissed = readDismissed();
        const requests = await fetchMyPendingApprovals();
        if (cancelled) return;
        setQueue(requests.filter((r) => !dismissed.includes(r.id)));
      } catch {
        // transient fetch failure -- next trigger (nav or backstop) retries
      }
    };
    checkRef.current = check;

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") check();
    }, BACKSTOP_POLL_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    checkRef.current();
  }, [location.pathname, isAdmin]);

  useEffect(() => {
    if (!active && queue.length > 0) {
      setActive(queue[0]);
    }
  }, [active, queue]);

  if (!isAdmin || !active) return null;

  const resolveAndAdvance = () => {
    setActive(null);
    setDecliningReason(null);
  };

  const handleClose = () => {
    dismiss(active.id);
    setQueue((prev) => prev.filter((r) => r.id !== active.id));
    resolveAndAdvance();
  };

  const handleApprove = async () => {
    try {
      setDeciding(true);
      await approveStatusChangeRequest(active.id);
      setQueue((prev) => prev.filter((r) => r.id !== active.id));
      showSuccessToast(`${active.booking_label} confirmed.`);
      resolveAndAdvance();
    } catch {
      showErrorToast("Failed to confirm booking.");
    } finally {
      setDeciding(false);
    }
  };

  const handleDeclineConfirm = async () => {
    try {
      setDeciding(true);
      await declineStatusChangeRequest(active.id, (decliningReason || "").trim());
      setQueue((prev) => prev.filter((r) => r.id !== active.id));
      showSuccessToast("Request declined.");
      resolveAndAdvance();
    } catch {
      showErrorToast("Failed to decline request.");
    } finally {
      setDeciding(false);
    }
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}
      >
        <HowToReg sx={{ color: "#4BA663" }} />
        Status Change Requested
        {active.requested_by?.first_name && ` by ${active.requested_by.first_name}`}
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            mb: 1.5,
            cursor: active.deceased_details ? "pointer" : "default",
            "&:hover": active.deceased_details
              ? { opacity: 0.85 }
              : undefined,
          }}
          onClick={(e) =>
            active.deceased_details &&
            setDeceasedAnchorEl(e.currentTarget)
          }
        >
          <Avatar
            src={active.deceased_image ?? undefined}
            sx={{ width: 44, height: 44 }}
          >
            {!active.deceased_image &&
              (active.deceased_name?.charAt(0).toUpperCase() ?? "?")}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
              {active.deceased_name ?? active.booking_label}
            </Typography>
            {active.deceased_name && (
              <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                {active.booking_label}
              </Typography>
            )}
          </Box>
        </Stack>

        <Popover
          open={Boolean(deceasedAnchorEl)}
          anchorEl={deceasedAnchorEl}
          onClose={() => setDeceasedAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Stack spacing={0.75} sx={{ p: 2, minWidth: 220 }}>
            {active.deceased_details?.relationship_to_deceased && (
              <DetailLine
                label="Relationship"
                value={active.deceased_details.relationship_to_deceased}
              />
            )}
            {active.deceased_details?.gender && (
              <DetailLine label="Gender" value={active.deceased_details.gender} />
            )}
            {active.deceased_details?.religion && (
              <DetailLine
                label="Religion"
                value={active.deceased_details.religion}
              />
            )}
            {active.deceased_details?.date_of_birth && (
              <DetailLine
                label="Date of Birth"
                value={formatDate(active.deceased_details.date_of_birth)}
              />
            )}
            {active.deceased_details?.date_of_death && (
              <DetailLine
                label="Date of Death"
                value={formatDate(active.deceased_details.date_of_death)}
              />
            )}
          </Stack>
        </Popover>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            bgcolor: "action.hover",
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            mb: 1.5,
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {capitalizeStatus(active.current_status)}
          </Typography>
          <ArrowForward sx={{ fontSize: 14, color: "text.secondary" }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#4BA663" }}>
            {capitalizeStatus(active.requested_status)}
          </Typography>
        </Stack>

        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
          {active.service && (
            <InfoRow icon={<CategoryOutlined />} text={active.service} />
          )}
          {active.service_date && (
            <InfoRow
              icon={<CalendarTodayOutlined />}
              text={formatDate(active.service_date)}
            />
          )}
          {active.venue_name && (
            <InfoRow icon={<LocationOnOutlined />} text={active.venue_name} />
          )}
        </Stack>

        {(active.contact_name || active.contact_phone) && (
          <Box
            sx={{
              mb: 1.5,
              cursor: active.contact_details ? "pointer" : "default",
              "&:hover": active.contact_details
                ? { opacity: 0.85 }
                : undefined,
            }}
            onClick={(e) =>
              active.contact_details && setContactAnchorEl(e.currentTarget)
            }
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: "text.secondary",
                mb: 0.5,
              }}
            >
              {active.contact_details?.role || "Client Contact"}
            </Typography>
            <Stack spacing={0.5}>
              {active.contact_name && (
                <InfoRow icon={<PersonOutline />} text={active.contact_name} />
              )}
              {active.contact_phone && (
                <InfoRow icon={<PhoneOutlined />} text={active.contact_phone} />
              )}
            </Stack>
          </Box>
        )}

        <Popover
          open={Boolean(contactAnchorEl)}
          anchorEl={contactAnchorEl}
          onClose={() => setContactAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Stack spacing={0.75} sx={{ p: 2, minWidth: 220 }}>
            {active.contact_details?.role && (
              <DetailLine label="Role" value={active.contact_details.role} />
            )}
            {active.contact_details?.email && (
              <DetailLine label="Email" value={active.contact_details.email} />
            )}
            {active.contact_details?.address && (
              <DetailLine
                label="Address"
                value={active.contact_details.address}
              />
            )}
          </Stack>
        </Popover>

        <Divider sx={{ mb: 1.5 }} />

        {active.requested_by && (
          <Stack direction="row" spacing={1.25}>
            <Avatar
              src={active.requested_by.profile_picture_url ?? undefined}
              sx={{ width: 32, height: 32 }}
            >
              {!active.requested_by.profile_picture_url &&
                (active.requested_by.first_name?.charAt(0).toUpperCase() ??
                  active.requested_by.username.charAt(0).toUpperCase())}
            </Avatar>
            <Box
              sx={{
                bgcolor: "action.hover",
                borderRadius: 3,
                px: 1.5,
                py: 1,
                flex: 1,
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                {`${active.requested_by.first_name} ${active.requested_by.last_name}`.trim() ||
                  active.requested_by.username}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}>
                {active.reason}
              </Typography>
            </Box>
          </Stack>
        )}
        {!active.requested_by && (
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {active.reason}
          </Typography>
        )}
        {queue.length > 1 && (
          <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 2 }}>
            +{queue.length - 1} more pending request
            {queue.length > 2 ? "s" : ""}
          </Typography>
        )}

        {decliningReason !== null && (
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason for declining (optional)"
            value={decliningReason}
            onChange={(e) => setDecliningReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {decliningReason !== null ? (
          <>
            <Button
              onClick={() => setDecliningReason(null)}
              disabled={deciding}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleDeclineConfirm}
              disabled={deciding}
              sx={{
                bgcolor: "#EB4335",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": { bgcolor: "#C5352A", boxShadow: "none" },
              }}
            >
              Confirm Decline
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => setDecliningReason("")}
              disabled={deciding}
              sx={{ color: "#EB4335", textTransform: "none", fontWeight: 600 }}
            >
              Decline
            </Button>
            <Button
              variant="contained"
              onClick={handleApprove}
              disabled={deciding}
              sx={{
                bgcolor: "#4BA663",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": { bgcolor: "#3E8A52", boxShadow: "none" },
              }}
            >
              Approve
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
